/** Panel CSS custom properties — light `:root` + dark `[data-theme='dark']` SSOT */

import { PANEL_PRIMITIVE } from "./panelTokens.primitives";

const v = (name) => `var(--panel-${name})`;

/** Semantic PANEL values consumed by sx modules — always CSS variables */
export const PANEL_CSS_REF = {
  pageBg: v("page-bg"),
  /** Dark: ModernDark gradient; light: none (solid pageBg) */
  pageGradient: v("page-gradient"),
  surface: v("surface"),
  popover: v("popover"),
  border: v("border"),
  text: {
    primary: v("text-primary"),
    body: v("text-body"),
    muted: v("text-muted"),
    label: v("text-label"),
    meta: v("text-meta"),
    title: v("text-title"),
  },
  accent: {
    main: v("accent"),
    hover: v("accent-hover"),
    text: v("accent-text"),
    focus: v("accent-focus"),
    contrast: v("accent-contrast"),
    soft: v("accent-soft"),
    soft12: v("accent-soft-12"),
    soft18: v("accent-soft-18"),
    softBorder: v("accent-soft-border"),
    softBorder22: v("accent-soft-border-22"),
    softBorder45: v("accent-soft-border-45"),
    focusRing: v("accent-focus-ring"),
    focusRing14: v("accent-focus-ring-14"),
    shadow: v("accent-shadow"),
    shadow25: v("accent-shadow-25"),
    hierarchyShadow: v("accent-hierarchy-shadow"),
  },
  blue: {
    main: v("blue"),
    text: v("blue-text"),
    soft: v("blue-soft"),
    soft12: v("blue-soft-12"),
    soft25: v("blue-soft-25"),
    soft30: v("blue-soft-30"),
    soft55: v("blue-soft-55"),
    chipBg: v("blue-chip"),
  },
  indigo: {
    main: v("indigo"),
    hover: v("indigo-hover"),
    text: v("indigo-text"),
    soft: v("indigo-soft"),
    softBorder: v("indigo-soft-border"),
  },
  danger: {
    main: v("danger"),
    soft: v("danger-soft"),
    border: v("danger-border"),
    text: v("danger-text"),
  },
  warning: {
    main: v("warning"),
    text: v("warning-text"),
    soft: v("warning-soft"),
    border: v("warning-border"),
  },
  fill: {
    muted: v("fill-muted"),
    subtle: v("fill-subtle"),
    hover: v("fill-hover"),
    hoverAlt: v("fill-hover-alt"),
  },
  chart: {
    1: v("chart-1"),
    2: v("chart-2"),
    3: v("chart-3"),
    4: v("chart-4"),
    5: v("chart-5"),
  },
  shadow: {
    card: v("shadow-card"),
    cardHover: v("shadow-card-hover"),
    elevated: v("shadow-elevated"),
    filterCard: v("shadow-filter-card"),
    filterCardHover: v("shadow-filter-card-hover"),
    elevatedMini: v("shadow-elevated-mini"),
    elevatedMiniHover: v("shadow-elevated-mini-hover"),
    dialog: v("shadow-dialog"),
    drawerMobile: v("shadow-drawer-mobile"),
    input: v("shadow-input"),
    inputHover: v("shadow-input-hover"),
    inputFocus: v("shadow-input-focus"),
  },
};

const P = PANEL_PRIMITIVE;

/**
 * Dark mode — ModernDarkColors (navy scaffold).
 * Base opaque: #060B14. Görünür zemin gradient; kartlar elevated navy.
 */
export const PANEL_MODERN_DARK = {
  backgroundBase: "#060B14",
  gradientMid: "#0A1628",
  gradientPeak: "#0E2138",
  gradientTail: "#0A1524",
  pageGradient:
    "linear-gradient(165deg, #060B14 0%, #0A1628 32%, #0E2138 68%, #0A1524 100%)",
  border: "rgba(148, 163, 184, 0.18)",
  borderSolid: "#1e3a5c",
  fillHover: "#143050",
  accentSoft: "rgba(255, 255, 255, 0.08)",
  accentSoftStrong: "rgba(255, 255, 255, 0.12)",
};

/** Light mode — mevcut slate / white / teal paleti */
export const PANEL_CSS_LIGHT = {
  "--panel-page-bg": P.slate100,
  "--panel-page-gradient": "none",
  "--panel-surface": P.white,
  "--panel-popover": P.white,
  "--panel-border": P.slate200,
  "--panel-text-primary": P.slate900,
  "--panel-text-body": P.slate700,
  "--panel-text-muted": P.slate500,
  "--panel-text-label": P.slate400,
  "--panel-text-meta": P.slate600,
  "--panel-text-title": P.slate800,
  "--panel-accent": P.tealAccent,
  "--panel-accent-hover": P.tealAccentHover,
  "--panel-accent-text": P.tealText,
  "--panel-accent-focus": P.tealFocus,
  "--panel-accent-contrast": P.slate900,
  "--panel-accent-soft": "rgba(76, 206, 172, 0.1)",
  "--panel-accent-soft-12": "rgba(76, 206, 172, 0.12)",
  "--panel-accent-soft-18": "rgba(76, 206, 172, 0.18)",
  "--panel-accent-soft-border": "rgba(76, 206, 172, 0.35)",
  "--panel-accent-soft-border-22": "rgba(76, 206, 172, 0.22)",
  "--panel-accent-soft-border-45": "rgba(76, 206, 172, 0.45)",
  "--panel-accent-focus-ring": "rgba(76, 206, 172, 0.2)",
  "--panel-accent-focus-ring-14": "rgba(76, 206, 172, 0.14)",
  "--panel-accent-shadow": "0 4px 12px rgba(76, 206, 172, 0.35)",
  "--panel-accent-shadow-25": "0 2px 8px rgba(76, 206, 172, 0.25)",
  "--panel-accent-hierarchy-shadow": "0 4px 16px rgba(76, 206, 172, 0.15)",
  "--panel-blue": P.blue500,
  "--panel-blue-text": P.blue600,
  "--panel-blue-soft": "rgba(59, 130, 246, 0.08)",
  "--panel-blue-soft-12": "rgba(59, 130, 246, 0.12)",
  "--panel-blue-soft-25": "rgba(59, 130, 246, 0.25)",
  "--panel-blue-soft-30": "rgba(59, 130, 246, 0.3)",
  "--panel-blue-soft-55": "rgba(59, 130, 246, 0.55)",
  "--panel-blue-chip": P.blue100,
  "--panel-indigo": P.indigo500,
  "--panel-indigo-hover": P.indigoHover,
  "--panel-indigo-text": P.indigoText,
  "--panel-indigo-soft": "rgba(79, 70, 229, 0.1)",
  "--panel-indigo-soft-border": "rgba(79, 70, 229, 0.28)",
  "--panel-indigo-400": P.indigo400,
  "--panel-indigo-soft-bg": P.indigoSoftBg,
  "--panel-indigo-400-border": "rgba(129, 140, 248, 0.35)",
  "--panel-danger": "#ef4444",
  "--panel-danger-soft": "rgba(239, 68, 68, 0.08)",
  "--panel-danger-border": "rgba(239, 68, 68, 0.35)",
  "--panel-danger-text": P.red600,
  "--panel-warning": "#d97706",
  "--panel-warning-text": "#92400e",
  "--panel-warning-soft": "rgba(251, 191, 36, 0.1)",
  "--panel-warning-border": "rgba(245, 158, 11, 0.32)",
  "--panel-fill-muted": P.slate50,
  "--panel-fill-subtle": P.slate100,
  "--panel-fill-hover": P.slate300,
  "--panel-fill-hover-alt": P.slate300Alt,
  "--panel-chart-1": "#4CCEAC",
  "--panel-chart-2": P.blue500,
  "--panel-chart-3": P.slate500,
  "--panel-chart-4": P.slate400,
  "--panel-chart-5": P.slate700,
  "--panel-gradient-end": P.formGradientEnd,
  "--panel-shadow-card": "0 1px 3px rgba(0,0,0,0.04)",
  "--panel-shadow-card-hover": "0 8px 24px rgba(15, 23, 42, 0.08)",
  "--panel-shadow-elevated": "0 20px 60px rgba(15, 23, 42, 0.08)",
  "--panel-shadow-filter-card":
    "0 4px 16px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.05)",
  "--panel-shadow-filter-card-hover":
    "0 8px 28px rgba(15, 23, 42, 0.11), 0 4px 10px rgba(15, 23, 42, 0.06)",
  "--panel-shadow-elevated-mini":
    "0 6px 20px rgba(15, 23, 42, 0.1), 0 2px 8px rgba(15, 23, 42, 0.06)",
  "--panel-shadow-elevated-mini-hover":
    "0 10px 28px rgba(15, 23, 42, 0.12), 0 4px 12px rgba(15, 23, 42, 0.08)",
  "--panel-shadow-dialog": "0 24px 48px rgba(15, 23, 42, 0.12)",
  "--panel-shadow-drawer-mobile": "4px 0 24px rgba(15, 23, 42, 0.08)",
  "--panel-shadow-input":
    "0 1px 2px rgba(15, 23, 42, 0.05), 0 2px 8px rgba(15, 23, 42, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
  "--panel-shadow-input-hover":
    "0 2px 6px rgba(15, 23, 42, 0.07), 0 4px 14px rgba(15, 23, 42, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
  "--panel-shadow-input-focus":
    "0 0 0 3px rgba(76, 206, 172, 0.14), 0 2px 10px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
};

/**
 * Dark mode — ModernDarkColors navy scaffold.
 * scaffoldBg transparent; görünür zemin base + page-gradient.
 * Hiyerarşi: base < fill/surface < popover. Accent dark'ta beyaz.
 */
export const PANEL_CSS_DARK = {
  "--panel-page-bg": PANEL_MODERN_DARK.backgroundBase,
  "--panel-page-gradient": PANEL_MODERN_DARK.pageGradient,
  "--panel-surface": PANEL_MODERN_DARK.gradientMid,
  "--panel-popover": PANEL_MODERN_DARK.gradientPeak,
  "--panel-border": PANEL_MODERN_DARK.border,
  "--panel-text-primary": "#ffffff",
  "--panel-text-body": "rgba(226, 232, 240, 0.92)",
  "--panel-text-muted": "rgba(148, 163, 184, 0.88)",
  "--panel-text-label": "rgba(148, 163, 184, 0.72)",
  "--panel-text-meta": "rgba(148, 163, 184, 0.78)",
  "--panel-text-title": "#ffffff",
  "--panel-accent": "#ffffff",
  "--panel-accent-hover": "rgba(226, 232, 240, 0.92)",
  "--panel-accent-text": "#ffffff",
  "--panel-accent-focus": "rgba(148, 163, 184, 0.88)",
  "--panel-accent-contrast": PANEL_MODERN_DARK.backgroundBase,
  "--panel-accent-soft": PANEL_MODERN_DARK.accentSoft,
  "--panel-accent-soft-12": PANEL_MODERN_DARK.accentSoft,
  "--panel-accent-soft-18": PANEL_MODERN_DARK.accentSoftStrong,
  "--panel-accent-soft-border": "rgba(148, 163, 184, 0.28)",
  "--panel-accent-soft-border-22": "rgba(148, 163, 184, 0.22)",
  "--panel-accent-soft-border-45": "rgba(148, 163, 184, 0.45)",
  "--panel-accent-focus-ring": "rgba(148, 163, 184, 0.35)",
  "--panel-accent-focus-ring-14": "rgba(148, 163, 184, 0.2)",
  "--panel-accent-shadow": "0 4px 12px rgba(6, 11, 20, 0.45)",
  "--panel-accent-shadow-25": "0 2px 8px rgba(6, 11, 20, 0.35)",
  "--panel-accent-hierarchy-shadow": "0 4px 16px rgba(6, 11, 20, 0.35)",
  "--panel-blue": "#4d7cff",
  "--panel-blue-text": "#93b4ff",
  "--panel-blue-soft": "rgba(77, 124, 255, 0.16)",
  "--panel-blue-soft-12": "rgba(77, 124, 255, 0.2)",
  "--panel-blue-soft-25": "rgba(77, 124, 255, 0.32)",
  "--panel-blue-soft-30": "rgba(77, 124, 255, 0.4)",
  "--panel-blue-soft-55": "rgba(77, 124, 255, 0.55)",
  "--panel-blue-chip": PANEL_MODERN_DARK.gradientTail,
  "--panel-indigo": "#9b87f5",
  "--panel-indigo-hover": "#8b74e8",
  "--panel-indigo-text": "#c4b5fd",
  "--panel-indigo-soft": "rgba(155, 135, 245, 0.18)",
  "--panel-indigo-soft-border": "rgba(155, 135, 245, 0.35)",
  "--panel-indigo-400": "#a78bfa",
  "--panel-indigo-soft-bg": "rgba(155, 135, 245, 0.18)",
  "--panel-indigo-400-border": "rgba(167, 139, 250, 0.35)",
  "--panel-danger": "#e5484d",
  "--panel-danger-soft": "rgba(229, 72, 77, 0.14)",
  "--panel-danger-border": "rgba(229, 72, 77, 0.4)",
  "--panel-danger-text": "#f87171",
  "--panel-warning": "#e8b86d",
  "--panel-warning-text": "#e8b86d",
  "--panel-warning-soft": "rgba(232, 184, 109, 0.14)",
  "--panel-warning-border": "rgba(232, 184, 109, 0.35)",
  "--panel-fill-muted": PANEL_MODERN_DARK.gradientTail,
  "--panel-fill-subtle": PANEL_MODERN_DARK.gradientPeak,
  "--panel-fill-hover": PANEL_MODERN_DARK.fillHover,
  "--panel-fill-hover-alt": PANEL_MODERN_DARK.gradientPeak,
  "--panel-chart-1": "#e8b86d",
  "--panel-chart-2": "#4d7cff",
  "--panel-chart-3": "rgba(148, 163, 184, 0.72)",
  "--panel-chart-4": "rgba(148, 163, 184, 0.45)",
  "--panel-chart-5": "#e2e8f0",
  "--panel-gradient-end": "#4d7cff",
  "--panel-shadow-card": "0 1px 3px rgba(6, 11, 20, 0.45)",
  "--panel-shadow-card-hover": "0 8px 24px rgba(6, 11, 20, 0.5)",
  "--panel-shadow-elevated": "0 20px 60px rgba(6, 11, 20, 0.5)",
  "--panel-shadow-filter-card":
    "0 4px 16px rgba(6, 11, 20, 0.4), 0 2px 6px rgba(6, 11, 20, 0.3)",
  "--panel-shadow-filter-card-hover":
    "0 8px 28px rgba(6, 11, 20, 0.48), 0 4px 10px rgba(6, 11, 20, 0.35)",
  "--panel-shadow-elevated-mini":
    "0 6px 20px rgba(6, 11, 20, 0.45), 0 2px 8px rgba(6, 11, 20, 0.35)",
  "--panel-shadow-elevated-mini-hover":
    "0 10px 28px rgba(6, 11, 20, 0.5), 0 4px 12px rgba(6, 11, 20, 0.4)",
  "--panel-shadow-dialog": "0 24px 48px rgba(6, 11, 20, 0.6)",
  "--panel-shadow-drawer-mobile": "4px 0 24px rgba(6, 11, 20, 0.5)",
  "--panel-shadow-input":
    "0 1px 2px rgba(6, 11, 20, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
  "--panel-shadow-input-hover":
    "0 2px 6px rgba(6, 11, 20, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
  "--panel-shadow-input-focus":
    "0 0 0 3px rgba(148, 163, 184, 0.28), 0 2px 10px rgba(6, 11, 20, 0.4)",
};

/**
 * MUI `createTheme` palette.main parse edilebilir renk ister (`var()` / `oklch()`
 * `augmentColor` içinde kırılır). Sx katmanı CSS değişkenlerini kullanır;
 * MUI palette bu hex yaklaşımlarıyla `palette.mode` motorunu çalıştırır.
 */
export const PANEL_MUI_LIGHT = {
  pageBg: P.slate100,
  surface: P.white,
  popover: P.white,
  border: P.slate200,
  textPrimary: P.slate900,
  textBody: P.slate700,
  textMuted: P.slate500,
  textLabel: P.slate400,
  accent: P.tealAccent,
  accentHover: P.tealAccentHover,
  accentText: P.tealText,
  accentSoft: "rgba(76, 206, 172, 0.1)",
  accentSoft12: "rgba(76, 206, 172, 0.12)",
  accentFocusRing: "rgba(76, 206, 172, 0.2)",
  accentContrast: P.slate900,
  indigo: P.indigo500,
  indigoHover: P.indigoHover,
  danger: "#ef4444",
  warning: "#d97706",
  blue: P.blue500,
  fillMuted: P.slate50,
  fillSubtle: P.slate100,
  fillHover: P.slate300,
};

export const PANEL_MUI_DARK = {
  pageBg: PANEL_MODERN_DARK.backgroundBase,
  surface: PANEL_MODERN_DARK.gradientMid,
  popover: PANEL_MODERN_DARK.gradientPeak,
  border: PANEL_MODERN_DARK.borderSolid,
  textPrimary: "#ffffff",
  textBody: "#e2e8f0",
  textMuted: "#94a3b8",
  textLabel: "#7c8ba1",
  accent: "#ffffff",
  accentHover: "#e2e8f0",
  accentText: "#ffffff",
  accentSoft: "#143050",
  accentSoft12: "#143050",
  accentFocusRing: "rgba(148, 163, 184, 0.35)",
  accentContrast: PANEL_MODERN_DARK.backgroundBase,
  indigo: "#9b87f5",
  indigoHover: "#8b74e8",
  danger: "#e5484d",
  warning: "#e8b86d",
  blue: "#4d7cff",
  fillMuted: PANEL_MODERN_DARK.gradientTail,
  fillSubtle: PANEL_MODERN_DARK.gradientPeak,
  fillHover: PANEL_MODERN_DARK.fillHover,
};

export function getPanelMuiPalette(mode = "light") {
  return mode === "dark" ? PANEL_MUI_DARK : PANEL_MUI_LIGHT;
}

export { getPanelLegacyColorsShim } from "./panelLegacyColorsShim";
export { default as usePanelLegacyColors } from "./usePanelLegacyColors";

export const panelCssVariableStyles = {
  ":root, [data-theme='light']": {
    colorScheme: "light",
    ...PANEL_CSS_LIGHT,
  },
  "[data-theme='dark']": {
    colorScheme: "dark",
    ...PANEL_CSS_DARK,
  },
};
